// Identity-vector interning — keeps a large inlined identity set from bloating
// the source cache key.
//
// A set-ref over a relation/identity column can inline hundreds of
// `{space, externalId}` instance refs. That vector rides `CdfViewNative.filter`
// (raw AST) into the `ProviderSource.native` blob, which IS the source cache
// key (`sourceCacheKey` in `materialize.ts`). Hashing the full vector per source
// is wasteful and grows the key unboundedly.
//
// Interning indirects the vector behind a small, stable, content-derived handle:
//
//   - `internedSetKey` derives the handle from the SORTED set of `identity.key`
//     values, so two inlinings of the same identity set — in any order — produce
//     the SAME handle (order-insensitive, content-addressed);
//   - the handle is registered in the session-scoped intern map
//     (`Session.intern`) against the real vector;
//   - the AST carries the handle (an `InternedValue` — just another opaque
//     `embedded` value per `astShape`) instead of the vector, so the source
//     cache key hashes the small handle;
//   - at fetch/translate time `resolveInternedNode` expands the handle back to
//     the real vector via `Session.resolveInterned`.
//
// Small vectors stay inline (below `INTERN_THRESHOLD`) — interning only earns
// its indirection on large sets.

import { type AstInput, astShape } from './normalize.ts';
import type { RowIdentityType } from './schema/row-identity.ts';

/**
 * Inline an identity vector behind a handle once its distinct-set size reaches
 * this threshold. Below it, the vector stays inline — the indirection is not
 * worth a small key. Chosen well under `SET_REF_MAX` so the common small-set
 * case is untouched.
 */
export const INTERN_THRESHOLD = 32;

/**
 * The interning carrier: a stable, content-derived handle standing in for a
 * large identity vector. It is an opaque `embedded` value (not a structural
 * marker), so `astShape` carries it through unchanged — its resolution is the
 * only thing indirected. The `$interned` key is the session intern-map key.
 */
export type InternedValue = {
  readonly $interned: string;
};

/** True iff `value` is an {@link InternedValue} handle. */
export const isInternedValue = (value: unknown): value is InternedValue =>
  typeof value === 'object' &&
  value !== null &&
  '$interned' in value &&
  typeof (value as { $interned: unknown }).$interned === 'string' &&
  !('name' in value);

/** FNV-1a over a string — a compact, deterministic, dependency-free digest. */
const fnv1a = (s: string): string =>
  ([...s].reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 0x01000193) >>> 0, 0x811c9dc5) >>> 0).toString(36);

/**
 * Content-derived handle key for an identity vector. Built from the SORTED set
 * of per-cell `identity.key` values so it is order-insensitive and stable: the
 * same identity SET always yields the same key regardless of row order, and the
 * member count is folded in to keep the digest robust. The identity contract is
 * the SSOT for how each cell is keyed — no struct field-name sniff here.
 */
export const internedSetKey = (refs: readonly unknown[], identity: RowIdentityType): string => {
  const keys = [...new Set(refs.map((ref) => identity.key(ref)))].sort();
  return `${identity.id}:${keys.length}:${fnv1a(keys.join(' '))}`;
};

/**
 * Resolve every {@link InternedValue} handle in `node` back to its real value
 * via `resolve`, rebuilding only the branches that contained a handle. Pure and
 * structural — handles only ever sit as `embedded` leaves (a comparison set
 * operand) or as a vector element, so the walk descends vectors and op-call
 * params and leaves every other shape untouched.
 */
export const resolveInternedNode = (node: AstInput, resolve: (key: string) => unknown): AstInput => {
  const shape = astShape(node);
  if (shape.kind === 'embedded') {
    return isInternedValue(shape.value) ? (resolve(shape.value.$interned) as AstInput) : node;
  }
  if (shape.kind === 'vector') {
    return shape.elements.map((el) => resolveInternedNode(el, resolve)) as readonly AstInput[] as AstInput;
  }
  if (shape.kind === 'opCall') {
    const { call } = shape;
    const params = call.params ?? [];
    return { ...call, params: params.map((p) => resolveInternedNode(p, resolve)) };
  }
  return node;
};
